import { useEffect, useRef, useState } from "react";
import { Blurhash } from "react-blurhash";
import { cn } from "~/lib/utils";
import { BLUR_HASH, FALLBACK_IMAGE_URL } from "~/utils/constant";
import ClientOnly from "./ClentOnly";

// Access the global image cache from the preloader hook
declare global {
  interface Window {
    __imageCache?: Map<string, { status: "loading" | "loaded" | "error" }>;
  }
}

const ImageWithSkeleton = ({
  url,
  alt = "",
  className = "",
  roundedClass = "",
  blur_hash = BLUR_HASH,
  loadImageLazy = true,
  width = 800,
  height = 500,
  isPreloaded = false,
  useSkeletonLoader = false,
}: {
  url: {
    small: string;
    medium: string;
    large: string;
    hd: string;
    original: string;
  };
  alt?: string;
  className?: string;
  roundedClass?: string;
  blur_hash?: string;
  width?: number;
  height?: number;
  loadImageLazy?: boolean;
  isPreloaded?: boolean;
  useSkeletonLoader?: boolean;
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const hasSetLoadedRef = useRef(false);
  const hasErroredRef = useRef(false);

  useEffect(() => {
    // Reset on URL change
    hasSetLoadedRef.current = false;
    hasErroredRef.current = false;
    setIsLoaded(false);

    if (isPreloaded) {
      hasSetLoadedRef.current = true;
      setIsLoaded(true);
      return;
    }

    const img = imgRef.current;
    if (!img) return;

    // If already loaded/cached
    if (img.complete && img.naturalWidth !== 0) {
      if (!hasSetLoadedRef.current) {
        hasSetLoadedRef.current = true;
        setIsLoaded(true);
      }
      return;
    }

    // Wait for image to load
    const handleLoad = () => {
      if (!hasSetLoadedRef.current) {
        hasSetLoadedRef.current = true;
        setIsLoaded(true);
      } else {
        console.log("Image load event fired again, ignoring.");
      }
    };

    img.addEventListener("load", handleLoad);
    return () => img.removeEventListener("load", handleLoad);
  }, [url.small, isPreloaded]);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // Prevent infinite loop - only handle error once
    if (hasErroredRef.current) {
      console.log("Error already handled, ignoring subsequent error events");
      return;
    }

    hasErroredRef.current = true;
    const target = e.currentTarget;

    // If we're already showing the fallback, don't try again
    if (target.src === FALLBACK_IMAGE_URL) {
      target.onerror = null; // Remove error handler to prevent any further attempts
      setIsLoaded(true); // Show whatever we have (blurhash will remain)
      return;
    }

    target.src = FALLBACK_IMAGE_URL;
    // Don't set onerror to null yet - let it try the fallback first
  };

  return (
    <ClientOnly
      fallback={
        <div className={cn("relative w-full h-full overflow-hidden ", roundedClass)}>
          {/* ✅ Skeleton loader OR Blurhash background - Hide immediately if preloaded */}
          {!isLoaded &&
            !isPreloaded &&
            loadImageLazy &&
            (useSkeletonLoader ? (
              <div className="w-full h-full bg-cardBg animate-pulse" />
            ) : (
              <Blurhash
                hash={blur_hash}
                width="100%"
                height="100%"
                resolutionX={32}
                resolutionY={32}
                punch={1}
                style={{ width: "100%", height: "100%", borderRadius: "inherit" }}
              />
            ))}
          {/* ✅ Final image shown via <picture> */}
          <picture>
            <source media="(min-width: 1024px)" srcSet={url.medium} />
            <source media="(min-width: 768px)" srcSet={url.medium} />
            <source media="(min-width: 640px)" srcSet={url.small} />
            <img
              ref={imgRef}
              src={url.small}
              title={alt || "Image"}
              alt={alt || "Skeleton Image"}
              width={width}
              height={height}
              loading={loadImageLazy && !isPreloaded ? "lazy" : "eager"}
              className={cn(
                "w-full h-full object-cover transition-opacity duration-500 select-none",
                className,
                // isLoaded || isPreloaded || typeof window === "undefined" ? "opacity-100" : "opacity-0",
              )}
              onError={handleImageError}
            />
          </picture>
        </div>
      }
    >
      <div className={cn("relative w-full h-full overflow-hidden ", roundedClass)}>
        {/* ✅ Skeleton loader OR Blurhash background - Hide immediately if preloaded */}
        {!isLoaded &&
          !isPreloaded &&
          (useSkeletonLoader ? (
            <div className="w-full h-full bg-cardBg animate-pulse" />
          ) : (
            <Blurhash
              hash={blur_hash}
              width="100%"
              height="100%"
              resolutionX={32}
              resolutionY={32}
              punch={1}
              style={{ width: "100%", height: "100%", borderRadius: "inherit" }}
            />
          ))}
        {/* ✅ Final image shown via <picture> */}
        <picture>
          <source
            media="(min-width: 1280px)"
            srcSet={`
      ${url.large} 1x,
      ${url.large} 2x
    `}
          />

          <source
            media="(min-width: 1024px)"
            srcSet={`
      ${url.small} 1x,
      ${url.medium} 2x
    `}
          />

          <source
            media="(min-width: 640px)"
            srcSet={`
      ${url.small} 1x,
      ${url.medium} 2x
    `}
          />

          <img
            ref={imgRef}
            srcSet={`
      ${url.small} 1x,
      ${url.medium} 2x
    `}
            title={alt || "Image"}
            alt={alt || "Skeleton Image"}
            width={width}
            height={height}
            loading={loadImageLazy && !isPreloaded ? "lazy" : "eager"}
            className={cn(
              "w-full h-full object-cover transition-opacity duration-500",
              className,
              // isLoaded || isPreloaded || typeof window === "undefined" ? "opacity-100" : "opacity-0",
            )}
            onError={handleImageError}
          />
        </picture>
      </div>
    </ClientOnly>
  );
};

export default ImageWithSkeleton;
